import { useRef, useState } from "react";
import { CheckCircle2, Upload, X } from "lucide-react";
import { Button, Dialog, Select } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export function ImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const suppliers = trpc.supplier.list.useQuery(undefined, { enabled: open });
  const fileRef = useRef<HTMLInputElement>(null);
  const pricesFileRef = useRef<HTMLInputElement>(null);
  const [supplierId, setSupplierId] = useState("");
  const format = "csv" as const;
  const [pricesFile, setPricesFile] = useState<{
    filename: string;
    base64: string;
  } | null>(null);
  const [result, setResult] = useState<{
    imported: number;
    updated: number;
    rowsParsed: number;
    warnings: string[];
  } | null>(null);

  const imp = trpc.article.importCatalog.useMutation({
    onSuccess: (r) => {
      setResult(r);
      utils.article.list.invalidate();
      utils.article.imports.invalidate();
      addToast({
        type: "success",
        title: `${r.imported} neu, ${r.updated} aktualisiert`,
      });
    },
    onError: (e) =>
      addToast({
        type: "error",
        title: "Import fehlgeschlagen",
        description: e.message,
      }),
  });

  const onPricesFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await fileToBase64(file);
    setPricesFile({ filename: file.name, base64 });
    e.target.value = "";
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !supplierId) return;
    const contentBase64 = await fileToBase64(file);
    imp.mutate({
      supplierId,
      filename: file.name,
      format,
      contentBase64,
      pricesFilename: pricesFile?.filename ?? null,
      pricesBase64: pricesFile?.base64 ?? null,
    });
    e.target.value = "";
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          setResult(null);
          setPricesFile(null);
        }
        onOpenChange(v);
      }}
      title="Artikel aus CSV importieren"
      wide
    >
      <div className="space-y-4">
        <Select
          label="Lieferant"
          value={supplierId}
          onChange={(e) => setSupplierId(e.target.value)}
          options={[
            { value: "", label: "— wählen —" },
            ...(suppliers.data ?? []).map((s) => ({
              value: s.id,
              label: s.name,
            })),
          ]}
        />
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.txt,text/csv,text/plain"
          className="hidden"
          onChange={onFile}
        />
        <Button
          variant="secondary"
          disabled={!supplierId}
          loading={imp.isPending}
          onClick={() => fileRef.current?.click()}
        >
          <Upload className="w-4 h-4" /> CSV-Datei wählen & importieren
        </Button>

        {result && (
          <div className="card p-3 text-sm space-y-1">
            <p className="text-text-primary">
              {result.rowsParsed} Zeilen gelesen · {result.imported} neu ·{" "}
              {result.updated} aktualisiert
            </p>
            {result.warnings.length > 0 && (
              <ul className="text-xs text-warning list-disc list-inside">
                {result.warnings.slice(0, 8).map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <p className="text-xs text-text-tertiary">
          CSV-Spalten: artikelnummer, name, einheit, ek_netto, listenpreis, ean,
          warengruppe. Datanorm-Kataloge (z. B. von Impex) werden über „Katalog
          aktualisieren" eingespielt.
        </p>
      </div>
    </Dialog>
  );
}
